import * as Popover from '@radix-ui/react-popover';
import { Field, useField } from 'formik';
import React, { useState } from 'react';
import { HexColorInput, HexColorPicker } from 'react-colorful';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useEffect } from 'react';
import clsx from 'clsx';

import useDebounce from '@dashboard/hooks/useDebounce';

export const ColorPicker = ({ value, name, onChange, defaultValue, presetColors = [] }) => {
  const [displayValue, setDisplayValue] = useState(value);
  const updateValue = useDebounce(displayValue, 100);

  useEffect(() => {
    if (updateValue !== value) {
      onChange(updateValue);
    }
  }, [updateValue]);

  useEffect(() => {
    setDisplayValue(value);
  }, [value]);

  return (
    <fieldgroup>
      <Popover.Root>
        <div className="flex">
          <div className="flex items-center border border-gray-200 overflow-hidden rounded w-36">
            <Popover.Trigger className="focus:outline-none block">
              <div
                className="h-10 w-10 border-r border-gray-100"
                style={{ backgroundColor: displayValue }}
              />
            </Popover.Trigger>

            <HexColorInput
              prefixed
              color={displayValue}
              onChange={setDisplayValue}
              className="pl-2 block w-full border-gray-300 rounded-md font-mono text-gray-700"
            />

            {value !== defaultValue && (
              <button
                onClick={() => setDisplayValue(defaultValue)}
                className="text-gray-300 hover:text-gray-500 px-1"
                type="button"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>

        <Popover.Portal>
          <Popover.Content side="bottom" align="start">
            <div className="flex flex-col">
              <HexColorPicker
                id={name}
                name={name}
                color={displayValue}
                onChange={setDisplayValue}
              />
            </div>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>

      <div className="mt-2 flex gap-x-2">
        {presetColors.map(presetColor => (
          <button
            key={presetColor}
            type="button"
            className={clsx('h-12 w-12 rounded border border-gray-200', {
              '!border-chocolate-500 border-2': presetColor === displayValue,
            })}
            style={{ background: presetColor }}
            onClick={() => setDisplayValue(presetColor)}
          />
        ))}
      </div>
    </fieldgroup>
  );
};

export const ColorPickerField = props => {
  // eslint-disable-next-line no-unused-vars
  const [field, meta, helpers] = useField(props);

  const { setValue } = helpers;

  const onChange = value => {
    setValue(value);

    if (props.onChange) {
      props.onChange(value);
    }
  };

  return <Field {...props} as={ColorPicker} onChange={onChange} value={field.value} />;
};
